import { MessageGroup } from './types';

type PayloadType = object | string;

// ToDo: Refactor this
export interface IMessage<Name extends string, Payload extends PayloadType> {
  (payload?: Payload): {
    name: Name;
    group?: string;
    payload: Partial<Payload> | undefined;
    options?: IMessageOptions<Payload>;
  };
  messageName: Name;
}

export interface IMessageOptions<Payload extends PayloadType> {
  defaultPayload?: Payload;
  group?: MessageGroup | string;
  payloadProcessor?: (rawPayload: Partial<Payload>) => Partial<Payload>;
  headless?: boolean;
}

export const createMessage = <Name extends string, Payload extends PayloadType>(
  name: Name,
  options?: IMessageOptions<Payload>
): IMessage<Name, Payload> => {
  const message: IMessage<Name, Payload> = (payload?: Partial<Payload>) => {
    if (options?.payloadProcessor) {
      payload = options.payloadProcessor(payload || {});
    }
    return { name, group: options?.group, payload: payload || options?.defaultPayload || undefined, options: options };
  };
  message.messageName = name;
  return message;
};

export interface IRegisteredMessage<TargetName extends string, Name extends string, Payload extends PayloadType> {
  targets: TargetName[];
  message: IMessage<Name, Payload>;
  registeredTargets: ITarget<TargetName>[];
}

export interface ITarget<TargetName extends string> {
  name: TargetName;
  origin: string | null;
  window?: Window;
  setOrigin: (origin: string) => void;
  setWindow: (window: Window) => void;
  reset: () => void;
}

export class MessageController<
  MessageName extends string,
  // MessagePayload extends object,
  RegisteredMessage extends IRegisteredMessage<TargetName, MessageName, any>,
  TargetName extends string,
> {
  public targets: Record<TargetName, ITarget<TargetName>> = {} as Record<TargetName, ITarget<TargetName>>;
  public registeredMessages: Record<RegisteredMessage['message']['messageName'], RegisteredMessage> = {} as Record<
    RegisteredMessage['message']['messageName'],
    RegisteredMessage
  >;

  constructor(
    targetNames: TargetName | TargetName[],
    messagesToRegister: Omit<RegisteredMessage, 'registeredTargets'>[]
  ) {
    if (!Array.isArray(targetNames)) {
      targetNames = [targetNames];
    }
    targetNames.forEach((targetName) => {
      this.targets[targetName] = {
        name: targetName,
        origin: null,
        setOrigin: (origin: string): void => this.setTargetOrigin(this.targets[targetName], origin),
        setWindow: (window: Window): void => this.setTargetWindow(this.targets[targetName], window),
        reset: (): void => this.resetTarget(this.targets[targetName]),
      };
    });
    messagesToRegister.forEach((messageToRegister) => {
      this.registeredMessages[messageToRegister.message.messageName] = {
        ...messageToRegister,
        registeredTargets: messageToRegister.targets.map((targetName) => this.targets[targetName]),
      } as RegisteredMessage;
    });
  }

  private setTargetWindow(target: ITarget<TargetName>, window: Window): void {
    target.window = window;
  }

  private setTargetOrigin(target: ITarget<TargetName>, origin: string): void {
    target.origin = origin;
  }

  private resetTarget(target: ITarget<TargetName>): void {
    target.origin = null;
    target.window = undefined;
  }

  post(message: ReturnType<RegisteredMessage['message']>): void {
    let registeredMessageTargets = this.registeredMessages[message.name].targets;
    const isHeadless = message.options?.headless || false;
    if (!Array.isArray(registeredMessageTargets)) {
      registeredMessageTargets = [registeredMessageTargets];
    }
    const messageTargets = registeredMessageTargets.map((targetName) => this.targets[targetName]);
    messageTargets.forEach((target) => {
      if (target?.origin && target?.window) {
        if (isHeadless) {
          target.window.postMessage(message.payload, target.origin);
        } else {
          target.window.postMessage(message, target.origin);
        }
      }
    });
  }
}
